import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import {
  ApprovalUserRef,
  ESignatureMethod,
  ReportESignatureRecord,
  SignoffRole,
} from 'types'

import { sha256Hex, toArrayBuffer } from './sha256Hex'
import { buildSignedPdfPath } from './signedPdfPaths'
import { stampSignature } from './stampSignature'
import { getTypedSignatureFont } from './typedSignature'

dayjs.extend(utc)

export interface SignRequest {
  role: SignoffRole
  method: ESignatureMethod
  signatureDataUrl: string
  signatureText?: string
  typedFontId?: string
  qualifications?: string
}

interface SignReportParams {
  request: SignRequest
  signer: ApprovalUserRef
  metadataEntryId: string
  sourcePath: string
  previousRecord: ReportESignatureRecord | undefined
  slotIndex: number
  fetchFileBytes: (path: string) => Promise<ArrayBuffer>
  uploadFile: (blob: Blob, path: string) => Promise<unknown>
}

export const toPdfBlob = (bytes: ArrayBuffer | Uint8Array) =>
  new Blob([toArrayBuffer(bytes)], { type: 'application/pdf' })

const describeMethod = (request: SignRequest, fontLabel: string) => {
  if (request.method === 'typed') {
    return `Method: typed "${request.signatureText ?? ''}" in the ${fontLabel} style`
  }
  return request.method === 'drawn'
    ? 'Method: drawn on screen'
    : 'Method: uploaded signature image'
}

/**
 * Stamps one signature onto the source PDF (the latest draft, or the previous
 * signer's output), uploads the result, and returns the audit record for it.
 * Verifies the source still matches the previous record's hash so a signer
 * never signs bytes nobody vouched for.
 */
export async function signReport({
  request,
  signer,
  metadataEntryId,
  sourcePath,
  previousRecord,
  slotIndex,
  fetchFileBytes,
  uploadFile,
}: SignReportParams): Promise<ReportESignatureRecord> {
  const sourceBytes = await fetchFileBytes(sourcePath)
  const sourceDocumentSha256 = await sha256Hex(sourceBytes)
  if (
    previousRecord &&
    previousRecord.signedDocumentSha256 !== sourceDocumentSha256
  ) {
    throw new Error(
      'The previously signed PDF no longer matches its recorded fingerprint. Void the signatures and sign again.'
    )
  }

  const signedAt = dayjs.utc()
  const font = getTypedSignatureFont(request.typedFontId)
  const signedBytes = await stampSignature(
    sourceBytes,
    request.signatureDataUrl,
    {
      role: request.role,
      signerName: signer.name,
      signerEmail: signer.email,
      qualifications: request.qualifications,
      methodDescription: describeMethod(request, font.label),
      signedAt: signedAt.toISOString(),
      sourceSha256: sourceDocumentSha256,
    },
    slotIndex
  )
  const signedDocumentPath = buildSignedPdfPath(
    metadataEntryId,
    signedAt.format('YYYYMMDD-HHmmss')
  )
  await uploadFile(toPdfBlob(signedBytes), signedDocumentPath)

  const isTyped = request.method === 'typed'
  return {
    id: crypto.randomUUID(),
    role: request.role,
    signerId: signer.userId,
    signerName: signer.name,
    signerEmail: signer.email,
    qualifications: request.qualifications || undefined,
    method: request.method,
    signatureText: isTyped ? request.signatureText : undefined,
    typedFont: isTyped ? font.id : undefined,
    signedAt: signedAt.toISOString(),
    sourceDocumentPath: sourcePath,
    sourceDocumentSha256,
    signedDocumentPath,
    signedDocumentSha256: await sha256Hex(signedBytes),
    userAgent: navigator.userAgent,
  }
}
